import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';

import { IPendingVisitsSearchQuery } from 'hooks/evv/useFetchPaginatedPendingVisits';

interface IUseVisitsTableHandlers {
  initialSearchQuery: IPendingVisitsSearchQuery;
}

export const useVisitsTableHandlers = ({ initialSearchQuery }: IUseVisitsTableHandlers) => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<IPendingVisitsSearchQuery>(initialSearchQuery);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearchInput = useCallback(
    debounce((searchValue: string) => {
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trimStart();
      setSearchInput(searchValue);
      debounceSearchInput(searchValue);
    },
    [debounceSearchInput, setSearchInput]
  );

  const handleDateChange = useCallback(
    (dates: any) => {
      const [startDate, endDate] = dates;

      if (startDate) {
        const startDateString = startDate ? format(startDate, 'yyyy-MM-dd') : null;
        const endDateString = endDate ? format(endDate, 'yyyy-MM-dd') : '';

        setSearchQuery({ ...searchQuery, page: 1, date_range: `${startDateString},${endDateString}` });
      } else {
        setSearchQuery({ ...searchQuery, page: 1, date_range: '' });
      }
    },
    [searchQuery, setSearchQuery]
  );

  const handlePageChange = useCallback(
    (selectedPageIndex: number) => {
      const updatedSearchQuery = (previousSearchQuery: IPendingVisitsSearchQuery) => ({
        ...previousSearchQuery,
        page: selectedPageIndex + 1,
      });
      setSearchQuery(updatedSearchQuery);
    },
    [setSearchQuery]
  );

  const handlePageSizeChange = (pageSize: number) => {
    setSearchQuery(queries => ({ ...queries, page_size: pageSize, page: 1 }));
  };

  const handleEmployeeFilterFormSubmit = useCallback(
    (selectedUserIds: any) => {
      const updatedEmployee = selectedUserIds.length ? selectedUserIds.map((item: any) => item.userId).join(',') : '';

      setSearchQuery((queries: any) => ({
        ...queries,
        user_ids: updatedEmployee,
      }));
    },
    [setSearchQuery]
  );

  return {
    searchInput,
    searchQuery,
    handlePageChange,
    handlePageSizeChange,
    handleSearchInputChange,
    handleDateChange,
    handleEmployeeFilterFormSubmit,
  };
};
